import { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { fetchProductChart } from '../../../../services/operations/forecastAPI';
import ProductImageModal from '../ProductImageModal';
import Breadcrumbs from '../../../common/Breadcrumbs';
import ProductCard from './ProductCard';
import Pagination from '../../../common/Pagination';
import ForecastHours from '../ForecastSidebarMenu/ForecastHours';
import { fetchForecastHours } from '../../../../services/operations/forecastAPI';
import Spinner from '../../../common/Spinner';
import ChangeUTC from './ChangeUTC';
import { setSpecialProductsId } from '../../../../slices/allSpecialProductsSliceId';

const FORECAST_HOURS = [0, 24, 48, 72, 96, 120, 144, 168, 192, 216, 240];
const utcOptions = [
  { label: '00 UTC', value: '00' },
  { label: '12 UTC', value: '12' },
];

const ModelProducts = () => {
  const selectedModel = useSelector((state) => state.forecast.selectedModel);
  const selectedProduct = useSelector(
    (state) => state.modelProducts.selectedProduct
  );
  const { selectedSpecialProductItem } = useSelector(
    (state) => state.specialProducts
  );
  const selectedProductId = useSelector(
    (state) => state.allSpecialProductsId.selectedProductId
  );
  const forecastUrl = useSelector(
    (state) => state.allSpecialProductsId.forecastUrl
  );
  // console.log('Selected Product ID from Redux:', selectedProductId);

  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [metaData, setMetaData] = useState([]);
  const [previewProduct, setPreviewProduct] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [forecastHours, setForecastHours] = useState(FORECAST_HOURS);
  const [selectedForecastHour, setSelectedForecastHour] = useState(
    forecastHours[0]
  );
  const [selectedUTC, setSelectedUTC] = useState(utcOptions[0]);
  console.log('setProducts', products);
  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const currentDate = new Date().toISOString().split('T')[0];
      const response = await fetchProductChart(
        selectedModel?.model_id,
        selectedProduct?.id,
        currentDate,
        selectedForecastHour,
        selectedUTC?.value,
        page,
        4,
        selectedProductId || 0
      );

      if (response.products.length > 0) {
        setProducts(response.products);
        setTotalPages(response.totalPages);
      } else if (response.metadata.length > 0) {
        setProducts([]);
        setMetaData(response.metadata);
        setTotalPages(0);
      } else {
        console.log('No forecast products available.');
        // toast.error('No forecast products available.');
      }
    } catch (error) {
      console.error('Error fetching forecast products:', error);
      toast.error(
        'Unexpected error occurred while fetching forecast products.'
      );
    } finally {
      setLoading(false);
    }
  }, [selectedModel, selectedProduct, selectedForecastHour, page, selectedUTC]);

  useEffect(() => {
    if (forecastUrl) {
      console.log('forecastUrl', forecastUrl);
      setProducts([forecastUrl]); // Store the URL in the products state
    }
  }, [forecastUrl]);

  useEffect(() => {
    setPage(1); // Reset page when product changes
  }, [selectedProduct]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    // console.log('ran useEffect for loadForecastHour');
    const loadForecastHour = async () => {
      setLoading(true);
      try {
        const { hours } = await fetchForecastHours(
          selectedModel?.model_id || 1,
          selectedProduct?.id || 2,
          selectedProductId ?? 0
        );
        // console.log('Fetched Forecast Hours:', hours);

        if (Array.isArray(hours) && hours.length > 0) {
          setForecastHours(hours);
          setSelectedForecastHour(hours[0]);
        } else {
          setForecastHours([]);
        }
      } catch (error) {
        console.error('Error fetching forecast hours:', error);
        toast.error('Unexpected error occurred while fetching forecast hours.');
      } finally {
        setLoading(false);
      }
    };

    loadForecastHour();
  }, [selectedModel?.model_id, selectedProduct?.id, selectedProductId]);

  useEffect(() => {
    dispatch(setSpecialProductsId(0)); // Reset selectedProductId after API call
  }, [selectedModel?.model_id, selectedProduct?.id]);

  return (
    <div className="h-screen overflow-auto p-6">
      {/* Breadcrumbs */}
      <Breadcrumbs
        selectedModel={selectedModel}
        selectedProduct={selectedProduct}
      />

      <div className="flex items-center gap-2">
        {/* UTC Change Component */}
        <div className="flex items-center">
          <ChangeUTC
            utcOptions={utcOptions}
            selectedUTC={selectedUTC}
            setSelectedUTC={setSelectedUTC}
          />
        </div>

        {/* Forecast Hours Selection */}
        {forecastHours.length > 0 && (
          <div className="flex items-center">
            <ForecastHours
              selectedHour={selectedForecastHour}
              onSelect={setSelectedForecastHour}
              forecastHours={forecastHours}
            />
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && <Spinner size="8" />}

      {/* No Products Available */}
      {!loading && products.length === 0 && metaData.length === 0 && (
        <p className="text-center text-lg text-slate-600">
          No forecast products available.
        </p>
      )}

      {/* Product Display Grid */}
      {!loading && products.length > 0 && (
        <div
          className={`grid gap-6 ${
            products.length === 1
              ? 'mx-auto grid-cols-1'
              : 'grid-cols-1 lg:grid-cols-2'
          }`}
        >
          {products.map((imageUrl, index) => (
            <ProductCard
              key={index}
              imageUrl={imageUrl}
              onClick={() => setPreviewProduct(imageUrl)}
              Class={products.length === 1 ? 'h-auto w-full' : ''}
            />
          ))}
        </div>
      )}

      {/* MetaData Display Grid */}
      {!loading && selectedProduct?.id === 5 && metaData.length > 0 && (
        <div
          className={`grid gap-6 ${
            products.length === 1
              ? 'mx-auto max-w-md grid-cols-1'
              : 'grid-cols-1 lg:grid-cols-2'
          }`}
        >
          {metaData.map((item, index) => (
            <ProductCard
              key={index}
              imageUrl={item.link}
              title={item.title}
              description={item.description}
              onClick={() => setPreviewProduct(item.link)}
              Class={products.length === 1 ? 'h-auto w-full' : ''}
            />
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      {!loading && totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}

      {/* Image Preview Modal */}
      {previewProduct && (
        <ProductImageModal
          imageUrl={previewProduct}
          onClose={() => setPreviewProduct(null)}
        />
      )}
    </div>
  );
};

export default ModelProducts;
